import './NavigationList.scss';

import {
  type Country,
  formatPhoneNumberRaw,
  isDefaultBrand,
  useConfig,
  useLocalStorage,
} from '@travelwin/core';
import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeaturedCountry } from '@/services/resources/ResourceService';

import { Link, useNavigate } from '../../../components/Link';
import LoginSignUpModal from '../../../components/LoginSignUpModal';
import { ROUTE_DEFAULT, STORAGE_ROUTE_KEY } from '../../../constants';
import { useUser } from '../../../context/User';
import { useCmsFooterItems } from '../../../hooks/useCmsFooterItems';
import { useCountryUtils } from '../../../hooks/useCountryUtils';
import ArrowDownIcon from './ArrowDownIcon';
import { FooterItem } from './types';
import { useAboutNavigationItem } from './useAboutNavigationItem';
import { useHelpNavigationItem } from './useHelpNavigationItem';
import { useShopNavigationItem } from './useShopNavigationItem';

const className = 'c-NavigationList';

const MAX_FEATURED_COUNTRIES = 8;

const NavigationItem = ({ isDropdown, title, list }: FooterItem) => {
  const [isOpen, setIsOpen] = useState(true);

  const handleDropDown = () => {
    if (isDropdown) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <section className={`${className}__section`}>
      <div
        className={classNames(`${className}__title`, {
          [`${className}__title--dropdown`]: isDropdown,
          [`${className}__title--open`]: isOpen,
        })}
        onMouseUp={handleDropDown}
        onFocus={handleDropDown}
      >
        <p>{title}</p>

        {isDropdown && <ArrowDownIcon />}
      </div>

      <ul
        className={classNames(`${className}__list`, {
          [`${className}__list--open`]: isOpen,
        })}
      >
        {list.map((content, index) => (
          <li className={`${className}__item`} key={`${title}-${index}`}>
            {content}
          </li>
        ))}
      </ul>
    </section>
  );
};

const NavigationList = ({
  featuredCountries,
  countries,
}: {
  featuredCountries?: FeaturedCountry[];
  countries?: Country[];
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    CONTACT_US_EMAIL_ADDRESS,
    CONTACT_US_PHONE_NUMBER,
    WHITELABEL_BRAND,
  } = useConfig();

  const { isAuthenticated, signOut } = useUser();
  const [isShowLogin, setIsShowLogin] = useState(false);
  const [redirectPath, setRedirectPath] = useLocalStorage(STORAGE_ROUTE_KEY);
  const { getCountryNameTranslation } = useCountryUtils();

  const handleCompleteLogin = () => {
    setIsShowLogin(false);
    const redirectRoute = redirectPath?.redirectAfterAuth
      ? redirectPath.redirectAfterAuth
      : ROUTE_DEFAULT;
    setRedirectPath(null);
    navigate(redirectRoute);
  };

  const contactContent: FooterItem = {
    title: t('footer.navigation.title.contact', 'Contacts:'),
    isDropdown: false,
    list: [
      <a
        key="mail"
        href={`mailto:${CONTACT_US_EMAIL_ADDRESS}`}
        data-testid="link-email"
        className={`${className}__link`}
      >
        {CONTACT_US_EMAIL_ADDRESS}
      </a>,
      !isDefaultBrand(WHITELABEL_BRAND) && CONTACT_US_PHONE_NUMBER && (
        <a
          key="phone"
          data-testid="link-phone"
          href={`tel:${formatPhoneNumberRaw(CONTACT_US_PHONE_NUMBER)}`}
          className={`${className}__link`}
        >
          {CONTACT_US_PHONE_NUMBER}
        </a>
      ),
    ],
  };

  const getFallbackCountries = () => {
    const fallbackCountries = ['GB', 'CA', 'IE', 'MX', 'TR', 'IN', 'US', 'PK'];
    return countries && countries.length > 0
      ? countries
          .filter((country) => fallbackCountries.includes(country.alias))
          .sort(
            (a, b) =>
              fallbackCountries.indexOf(a.alias) -
              fallbackCountries.indexOf(b.alias),
          )
      : [];
  };

  const displayFeaturedCountries = () => {
    return featuredCountries && featuredCountries.length > 0
      ? featuredCountries.slice(0, MAX_FEATURED_COUNTRIES)
      : getFallbackCountries();
  };

  const destinationItem: FooterItem = {
    title: t('footer.navigation.title.destination', 'Destination'),
    isDropdown: true,
    list: displayFeaturedCountries().map((country) => (
      <Link
        key={country.english_name}
        className={`${className}__link`}
        to={`/plan-selection/${country.slug}`}
      >
        {getCountryNameTranslation(country.description)}
      </Link>
    )),
  };

  const shopItem = useShopNavigationItem(className);
  const aboutItem = useAboutNavigationItem(className);
  const helpItem = useHelpNavigationItem(className);
  const cmsItems = useCmsFooterItems(className);
  const whitelabelShopItem: FooterItem = {
    title: t('navbar.shop', 'Shop'),
    isDropdown: false,
    list: [
      <Link key="my_esim" className={`${className}__link`} to="/orders">
        {t('navbar.my_esim', 'My eSIM')}
      </Link>,
      isAuthenticated ? (
        <div
          key="log_out"
          role="button"
          className={`${className}__link`}
          onMouseUp={() => signOut()}
          onFocus={() => signOut()}
        >
          {t('navbar.logout', 'Log Out')}
        </div>
      ) : (
        <div
          key="log_in"
          role="button"
          className={`${className}__link`}
          onMouseUp={() => setIsShowLogin(true)}
          onFocus={() => setIsShowLogin(true)}
        >
          {t('navbar.login', 'Log In')}
        </div>
      ),
      <Link key="my_account" className={`${className}__link`} to="/my-account">
        {t('navbar.my_account', 'My Account')}
      </Link>,
    ],
  };

  const footerItems = cmsItems ?? [aboutItem, helpItem];

  const contents: FooterItem[] = isDefaultBrand(WHITELABEL_BRAND)
    ? [destinationItem, shopItem, ...footerItems, contactContent]
    : [whitelabelShopItem, contactContent];

  return (
    <div className={className}>
      {contents.map((navigationItem) => (
        <NavigationItem key={navigationItem.title} {...navigationItem} />
      ))}

      <LoginSignUpModal
        on={isShowLogin}
        toggle={() => setIsShowLogin(false)}
        handleCompleted={handleCompleteLogin}
      />
    </div>
  );
};

export default NavigationList;
